import { createAsyncThunk } from '@reduxjs/toolkit'
import { taskTrackerApi } from '../../api/taskTracker.api'
import {
  buildOnCompleteAutomationUpdatePayload,
  buildOnMoveAutomationUpdatePayload,
  findAutomationByTrigger,
  mapCompletedAutomationToDraft,
  mapMovedAutomationToDraft,
  serializeBoardAutomationOnCompleteDraft,
  serializeBoardAutomationOnMoveDraft,
  TASK_TRACKER_AUTOMATION_TRIGGER_COMPLETED,
  TASK_TRACKER_AUTOMATION_TRIGGER_MOVED,
} from '../../lib/taskTrackerBoardAutomations.utils'
import type { TaskTrackerBoardAutomation } from '../domain.types'
import { getErrorMessage } from '../shared/thunk.utils'
import type { BoardAutomationsBoardState } from './boardAutomations.types'
import type { RootState } from '@store/redux.store'

const applyAutomationsDetails = (
  details: Awaited<ReturnType<typeof taskTrackerApi.getBoardAutomations>>,
) => {
  const completeAutomation = findAutomationByTrigger(
    details.automations,
    TASK_TRACKER_AUTOMATION_TRIGGER_COMPLETED,
  )
  const moveAutomation = findAutomationByTrigger(
    details.automations,
    TASK_TRACKER_AUTOMATION_TRIGGER_MOVED,
  )
  const onCompleteDraft = mapCompletedAutomationToDraft(completeAutomation)
  const onMoveDraft = mapMovedAutomationToDraft(moveAutomation)

  return {
    availableColumns: details.availableColumns,
    completeAutomation,
    moveAutomation,
    onCompleteDraft,
    onMoveDraft,
    savedOnCompleteDraft: onCompleteDraft,
    savedOnMoveDraft: onMoveDraft,
  }
}

export const fetchBoardAutomationsThunk = createAsyncThunk(
  'taskTracker/fetchBoardAutomations',
  async (boardId: number, { rejectWithValue }) => {
    try {
      const details = await taskTrackerApi.getBoardAutomations(boardId)
      return { boardId, ...applyAutomationsDetails(details) }
    } catch (error: unknown) {
      return rejectWithValue({
        boardId,
        error: getErrorMessage(error, 'Не удалось загрузить автоматизации'),
      })
    }
  },
)

export const saveBoardAutomationsThunk = createAsyncThunk(
  'taskTracker/saveBoardAutomations',
  async (boardId: number, { getState, rejectWithValue }) => {
    const state = getState() as RootState & {
      taskTracker: { boardAutomations: { byBoardId: Record<number, BoardAutomationsBoardState> } }
    }
    const autoState = state.taskTracker.boardAutomations.byBoardId[boardId]
    if (!autoState) return null

    const isCompleteDirty =
      serializeBoardAutomationOnCompleteDraft(autoState.onCompleteDraft) !==
      serializeBoardAutomationOnCompleteDraft(autoState.savedOnCompleteDraft)
    const isMoveDirty =
      serializeBoardAutomationOnMoveDraft(autoState.onMoveDraft) !==
      serializeBoardAutomationOnMoveDraft(autoState.savedOnMoveDraft)

    try {
      const completePayload = isCompleteDirty
        ? buildOnCompleteAutomationUpdatePayload(
            autoState.onCompleteDraft,
            autoState.completeAutomation,
          )
        : null
      const movePayload = isMoveDirty
        ? buildOnMoveAutomationUpdatePayload(
            autoState.onMoveDraft,
            autoState.moveAutomation,
          )
        : null

      if (completePayload == null && movePayload == null) return null

      let details: Awaited<
        ReturnType<typeof taskTrackerApi.getBoardAutomations>
      > | null = null

      if (completePayload != null) {
        details = await taskTrackerApi.updateBoardAutomations(
          boardId,
          completePayload,
        )
      }

      if (movePayload != null) {
        details = await taskTrackerApi.updateBoardAutomations(
          boardId,
          movePayload,
        )
      }

      if (details == null) return null

      return { boardId, ...applyAutomationsDetails(details) }
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, 'Не удалось сохранить автоматизации'),
      )
    }
  },
)

export type { TaskTrackerBoardAutomation }
